import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { IconButton, Tooltip } from '@mui/material';
import { AiSparkleIcon } from '@sharedCR/coreDesignSystem/icons';
import { ModalSize } from '@sharedCR/coreDesignSystem/Modals/ModalCDS/ModalCDS.constants';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { FeatureFlag } from 'constants/featureFlags';

import { selectIsAiExpressionExplanationsEnabled } from 'store/companies/selectors';
import { openDialogAction } from 'store/global/actions';

import {
  EXPLAIN_BUTTON_LABEL,
  EXPLAIN_INLINE_SCHEMA_DOMAIN,
  EXPLAIN_MODAL_TITLE,
} from './ExplainExpression.constants';
import { ExplainExpressionTestIds } from './ExplainExpression.testIds';
import { serializeSchemaForExplain, tryBuildExpressionTree } from './ExplainExpression.utils';
import ExplainExpressionModalBody from './ExplainExpressionModalBody';

const ExplainExpressionButton = ({
  schemaParams,
  value,
  schemaDomain = EXPLAIN_INLINE_SCHEMA_DOMAIN,
  isOnlyBooleanAllowed = false,
  disabled = false,
}) => {
  const dispatch = useDispatch();
  const isAiExpressionExplanationsEnabled = useSelector(selectIsAiExpressionExplanationsEnabled);
  const isNewExpressionsParsingLogicEnabled = useFeatureToggle(
    FeatureFlag.EnableNewExpressionsParsingLogic,
  );

  const onClick = useCallback(() => {
    const expressionJson = tryBuildExpressionTree({
      userFriendlyExpression: value,
      schemaParams,
      isOnlyBooleanAllowed,
      isNewExpressionsParsingLogicEnabled,
    });
    const schemaJson = !_.isEmpty(schemaParams) ? serializeSchemaForExplain(schemaParams) : null;

    dispatch(
      openDialogAction({
        header: EXPLAIN_MODAL_TITLE,
        isRestyled: true,
        size: ModalSize.LARGE,
        body: () => (
          <ExplainExpressionModalBody
            expressionJson={expressionJson}
            expressionText={value || null}
            schemaDomain={schemaDomain}
            schemaJson={schemaJson}
          />
        ),
      }),
    );
  }, [
    dispatch,
    value,
    schemaParams,
    schemaDomain,
    isOnlyBooleanAllowed,
    isNewExpressionsParsingLogicEnabled,
  ]);

  if (!isAiExpressionExplanationsEnabled) {
    return null;
  }

  return (
    <Tooltip placement="top" title={EXPLAIN_BUTTON_LABEL}>
      <span>
        <IconButton
          data-testid={ExplainExpressionTestIds.BUTTON}
          disabled={disabled}
          size="small"
          onClick={onClick}
        >
          <AiSparkleIcon size={IconSize[20]} />
        </IconButton>
      </span>
    </Tooltip>
  );
};

ExplainExpressionButton.propTypes = {
  schemaParams: PropTypes.object,
  schemaDomain: PropTypes.string,
  value: PropTypes.string,
  isOnlyBooleanAllowed: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default ExplainExpressionButton;
